import React, { useState, useEffect } from 'react';
import CompanyLayout from '../components/CompanyLayout';
import { QUERY_SEEDS, QUERY_SEED_LABELS } from '../../constants/querySeeds';
import { jobApiService, Job } from '../../recruiter/services/jobApi';

interface Candidate {
  id: string;
  name: string;
  email: string;
  yearsExp: number;
  skills: string[];
  allJobTitles: string[];
  allCompanies: string[];
  degreesList: string[];
}

interface CandidateMatch {
  candidate: Candidate;
  matchScore: number;
  vectorSimilarity: number;
  qwenScore?: number;
  softCriteriaScore: number;
}

interface SearchResponse {
  query: string;
  totalCandidates: number;
  matches: CandidateMatch[];
  processingTimeMs: number;
}

const CandidateSearch: React.FC = () => {
  const [customQuery, setCustomQuery] = useState('');
  const [selectedSeed, setSelectedSeed] = useState<string | null>(null);
  const [searchResults, setSearchResults] = useState<SearchResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [infoSeedKey, setInfoSeedKey] = useState<string | null>(null);

  // Job selection
  const [jobs, setJobs] = useState<Job[]>([]);
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const [loadingJobs, setLoadingJobs] = useState(false);

  // Custom search filters
  const [showFilters, setShowFilters] = useState(false);
  const [minYearsExp, setMinYearsExp] = useState<string>('');
  const [skillsFilter, setSkillsFilter] = useState<string>('');
  const [titlesFilter, setTitlesFilter] = useState<string>('');
  const [companiesFilter, setCompaniesFilter] = useState<string>('');
  const [locationsFilter, setLocationsFilter] = useState<string>('');

  const resultsPerPage = 10;

  // Fetch recruiter's jobs on mount
  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    try {
      setLoadingJobs(true);

      // Get current user ID from localStorage
      const userDataString = localStorage.getItem('authUser');
      if (!userDataString) {
        console.warn('No auth user found, cannot filter jobs');
        setJobs([]);
        return;
      }

      const userData = JSON.parse(userDataString);
      const userId = userData.id || userData._id || userData.user_id;

      if (!userId) {
        console.warn('No user ID found in auth user');
        setJobs([]);
        return;
      }

      // Fetch all jobs
      const { jobs: allJobs } = await jobApiService.getAllJobs({ limit: 100 });

      // Filter jobs posted by the current user
      const userJobs = allJobs.filter((job) => {
        const postedBy = job.postedBy || job.recruiterId || job.companyId;
        return postedBy === userId;
      });

      console.log(`Filtered to ${userJobs.length} jobs posted by current user`);
      setJobs(userJobs);
    } catch (err) {
      console.error('Failed to fetch jobs:', err);
    } finally {
      setLoadingJobs(false);
    }
  };

  const handleSeedClick = async (seedKey: string) => {
    setSelectedSeed(seedKey);
    setCustomQuery('');
    setError(null);
    setCurrentPage(1);

    // Clear custom filters
    setMinYearsExp('');
    setSkillsFilter('');
    setTitlesFilter('');
    setCompaniesFilter('');
    setLocationsFilter('');
    setShowFilters(false);

    const seedConfig = QUERY_SEEDS[seedKey];
    await performSearch(seedConfig.query, seedConfig.hardCriteria, seedConfig.softCriteria);
  };

  const handleSearchForJob = async () => {
    if (!selectedJobId) {
      setError('Please select a job to search candidates for');
      return;
    }

    const selectedJob = jobs.find(j => j.id === selectedJobId);
    if (!selectedJob) {
      setError('Selected job not found');
      return;
    }

    setSelectedSeed(null);
    setError(null);
    setCurrentPage(1);

    // Build query from job details
    let query = `Searching for candidates for ${selectedJob.title}. `;
    if (selectedJob.description) {
      query += selectedJob.description.substring(0, 200) + '... ';
    }

    // Build hard criteria from job
    const hardCriteria: any = {};
    if (selectedJob.requirements?.yearsExperience) {
      hardCriteria.minYearsExperience = selectedJob.requirements.yearsExperience;
    }
    if (selectedJob.requirements?.requiredSkills && selectedJob.requirements.requiredSkills.length > 0) {
      hardCriteria.requiredSkills = selectedJob.requirements.requiredSkills;
    }

    // Build soft criteria from job
    const softCriteria: any = {};
    const allSkills = [
      ...(selectedJob.requirements?.requiredSkills || []),
      ...(selectedJob.requirements?.preferredSkills || [])
    ];
    if (allSkills.length > 0) {
      softCriteria.keywords = allSkills;
    }

    await performSearch(
      query,
      Object.keys(hardCriteria).length > 0 ? hardCriteria : undefined,
      Object.keys(softCriteria).length > 0 ? softCriteria : undefined
    );
  };

  const generateQueryFromFilters = () => {
    let query = customQuery.trim();
    const filterParts: string[] = [];

    if (skillsFilter.trim()) {
      const skills = skillsFilter.split(',').map(s => s.trim()).filter(s => s);
      if (skills.length > 0) {
        filterParts.push(`with skills in ${skills.join(', ')}`);
      }
    }

    if (titlesFilter.trim()) {
      const titles = titlesFilter.split(',').map(t => t.trim()).filter(t => t);
      if (titles.length > 0) {
        filterParts.push(`preferably with job titles like ${titles.join(', ')}`);
      }
    }

    if (companiesFilter.trim()) {
      const companies = companiesFilter.split(',').map(c => c.trim()).filter(c => c);
      if (companies.length > 0) {
        filterParts.push(`from companies like ${companies.join(', ')}`);
      }
    }

    if (locationsFilter.trim()) {
      const locations = locationsFilter.split(',').map(l => l.trim()).filter(l => l);
      if (locations.length > 0) {
        filterParts.push(`located in ${locations.join(', ')}`);
      }
    }

    if (minYearsExp.trim() && !isNaN(Number(minYearsExp))) {
      filterParts.push(`with at least ${minYearsExp} years of experience`);
    }

    if (filterParts.length > 0) {
      query = query ? `${query} ${filterParts.join(', ')}` : filterParts.join(', ');
    }

    return query;
  };

  const handleCustomSearch = async () => {
    const finalQuery = generateQueryFromFilters();

    if (!finalQuery.trim()) {
      setError('Please enter a search query or select filters');
      return;
    }

    setSelectedSeed(null);
    setSelectedJobId(null);
    setError(null);
    setCurrentPage(1);

    const hardCriteria: any = {};
    const softCriteria: any = {};

    if (minYearsExp.trim() && !isNaN(Number(minYearsExp))) {
      hardCriteria.minYearsExperience = Number(minYearsExp);
    }

    if (skillsFilter.trim()) {
      const skills = skillsFilter.split(',').map(s => s.trim()).filter(s => s);
      if (skills.length > 0) {
        softCriteria.keywords = skills;
      }
    }

    if (titlesFilter.trim()) {
      const titles = titlesFilter.split(',').map(t => t.trim()).filter(t => t);
      if (titles.length > 0) {
        softCriteria.preferredTitles = titles;
      }
    }

    if (companiesFilter.trim()) {
      const companies = companiesFilter.split(',').map(c => c.trim()).filter(c => c);
      if (companies.length > 0) {
        softCriteria.preferredCompanies = companies;
      }
    }

    if (locationsFilter.trim()) {
      const locations = locationsFilter.split(',').map(l => l.trim()).filter(l => l);
      if (locations.length > 0) {
        hardCriteria.requiredLocations = locations;
      }
    }

    await performSearch(
      finalQuery,
      Object.keys(hardCriteria).length > 0 ? hardCriteria : undefined,
      Object.keys(softCriteria).length > 0 ? softCriteria : undefined
    );
  };

  const handleShowInfo = (seedKey: string) => {
    setInfoSeedKey(seedKey);
    setShowInfoModal(true);
  };

  const performSearch = async (
    query: string,
    hardCriteria?: any,
    softCriteria?: any
  ) => {
    setLoading(true);
    setError(null);

    try {
      const matchServiceUrl = import.meta.env.VITE_MATCHING_SERVICE_URL || 'http://localhost:8004';
      const response = await fetch(`${matchServiceUrl}/matches/v1/search-candidates`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query,
          hardCriteria,
          softCriteria,
          limit: 50
        }),
      });

      if (!response.ok) {
        throw new Error(`Search failed: ${response.statusText}`);
      }

      const data: SearchResponse = await response.json();
      setSearchResults(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred during search');
      setSearchResults(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <CompanyLayout>
      <div className="space-y-6">
        {/* Header Card */}
        <div className="bg-white rounded-2xl p-6">
          <h2 className="text-2xl font-semibold text-[#0B111E] mb-2">AI-Powered Candidate Search</h2>
          <p className="text-sm text-[#757575]">Search through candidates using advanced AI matching for your job postings</p>
        </div>

        {/* Job Selection */}
        <div className="bg-white rounded-2xl p-6">
          <h3 className="text-lg font-semibold text-[#0B111E] mb-4">Search Candidates for Your Jobs</h3>
          {loadingJobs ? (
            <p className="text-sm text-[#757575]">Loading your jobs...</p>
          ) : jobs.length === 0 ? (
            <p className="text-sm text-[#757575]">No jobs found. Create a job first to search for candidates.</p>
          ) : (
            <div className="flex gap-3">
              <select
                value={selectedJobId || ''}
                onChange={(e) => setSelectedJobId(e.target.value)}
                className="flex-1 px-4 py-3 rounded-lg bg-[#F1F3F4] text-[#0B111E] border border-[#EDEDED] focus:outline-none focus:border-[#015FA6]"
                disabled={loading}
              >
                <option value="">Select a job to find candidates...</option>
                {jobs.map((job) => (
                  <option key={job.id} value={job.id}>
                    {job.title} {job.location && `- ${typeof job.location === 'string' ? job.location : job.location.city}`}
                  </option>
                ))}
              </select>
              <button
                onClick={handleSearchForJob}
                disabled={loading || !selectedJobId}
                className="px-6 py-3 bg-[#015FA6] hover:bg-[#014A85] disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors"
              >
                {loading ? 'Searching...' : 'Find Candidates'}
              </button>
            </div>
          )}
        </div>

        {/* Quick Search Templates */}
        <div className="bg-white rounded-2xl p-6">
          <h3 className="text-lg font-semibold text-[#0B111E] mb-4">Quick Search Templates</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {Object.keys(QUERY_SEEDS).map((seedKey) => (
              <div key={seedKey} className="relative">
                <button
                  onClick={() => handleSeedClick(seedKey)}
                  className={`w-full px-4 py-3 rounded-lg font-medium transition-all text-sm ${
                    selectedSeed === seedKey
                      ? 'bg-[#015FA6] text-white shadow-sm'
                      : 'bg-[#F1F3F4] hover:bg-[#E3F2FD] text-[#0B111E]'
                  }`}
                  disabled={loading}
                >
                  {QUERY_SEED_LABELS[seedKey]}
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleShowInfo(seedKey);
                  }}
                  className="absolute top-2 right-2 p-1 rounded-full bg-white/80 hover:bg-white transition-colors"
                  title="View search criteria"
                >
                  <svg className="w-4 h-4 text-[#757575]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Custom Search */}
        <div className="bg-white rounded-2xl p-6">
          <h3 className="text-lg font-semibold text-[#0B111E] mb-4">Custom Search</h3>
          <div className="space-y-4">
            <div className="flex gap-3">
              <input
                type="text"
                value={customQuery}
                onChange={(e) => setCustomQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && !showFilters && handleCustomSearch()}
                placeholder="Enter your custom search query (e.g., 'Looking for software engineers')..."
                className="flex-1 px-4 py-3 rounded-lg bg-[#F1F3F4] text-[#0B111E] placeholder-[#A5A5A5] border border-[#EDEDED] focus:outline-none focus:border-[#015FA6]"
                disabled={loading}
              />
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="px-4 py-3 bg-[#F1F3F4] hover:bg-[#E3F2FD] text-[#0B111E] font-medium rounded-lg transition-colors border border-[#EDEDED]"
                disabled={loading}
              >
                {showFilters ? 'Hide Filters' : 'Show Filters'}
              </button>
              <button
                onClick={handleCustomSearch}
                disabled={loading}
                className="px-6 py-3 bg-[#015FA6] hover:bg-[#014A85] disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors"
              >
                {loading ? 'Searching...' : 'Search'}
              </button>
            </div>

            {/* Filters */}
            {showFilters && (
              <div className="bg-[#F9F9F9] border border-[#EDEDED] rounded-lg p-4 space-y-4">
                <h4 className="text-sm font-semibold text-[#0B111E]">Search Filters</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#515151] mb-2">
                      Minimum Years of Experience
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={minYearsExp}
                      onChange={(e) => setMinYearsExp(e.target.value)}
                      placeholder="e.g., 3"
                      className="w-full px-4 py-2 rounded-lg bg-white text-[#0B111E] placeholder-[#A5A5A5] border border-[#EDEDED] focus:outline-none focus:border-[#015FA6]"
                      disabled={loading}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#515151] mb-2">
                      Skills (comma-separated)
                    </label>
                    <input
                      type="text"
                      value={skillsFilter}
                      onChange={(e) => setSkillsFilter(e.target.value)}
                      placeholder="e.g., Python, AWS, Docker"
                      className="w-full px-4 py-2 rounded-lg bg-white text-[#0B111E] placeholder-[#A5A5A5] border border-[#EDEDED] focus:outline-none focus:border-[#015FA6]"
                      disabled={loading}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#515151] mb-2">
                      Preferred Job Titles (comma-separated)
                    </label>
                    <input
                      type="text"
                      value={titlesFilter}
                      onChange={(e) => setTitlesFilter(e.target.value)}
                      placeholder="e.g., Software Engineer, Senior Developer"
                      className="w-full px-4 py-2 rounded-lg bg-white text-[#0B111E] placeholder-[#A5A5A5] border border-[#EDEDED] focus:outline-none focus:border-[#015FA6]"
                      disabled={loading}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#515151] mb-2">
                      Preferred Companies (comma-separated)
                    </label>
                    <input
                      type="text"
                      value={companiesFilter}
                      onChange={(e) => setCompaniesFilter(e.target.value)}
                      placeholder="e.g., Google, Microsoft, Amazon"
                      className="w-full px-4 py-2 rounded-lg bg-white text-[#0B111E] placeholder-[#A5A5A5] border border-[#EDEDED] focus:outline-none focus:border-[#015FA6]"
                      disabled={loading}
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-[#515151] mb-2">
                      Locations (comma-separated)
                    </label>
                    <input
                      type="text"
                      value={locationsFilter}
                      onChange={(e) => setLocationsFilter(e.target.value)}
                      placeholder="e.g., San Francisco, New York, Remote"
                      className="w-full px-4 py-2 rounded-lg bg-white text-[#0B111E] placeholder-[#A5A5A5] border border-[#EDEDED] focus:outline-none focus:border-[#015FA6]"
                      disabled={loading}
                    />
                  </div>
                </div>

                {/* Generated Query Preview */}
                {(customQuery.trim() || minYearsExp.trim() || skillsFilter.trim() || titlesFilter.trim() || companiesFilter.trim() || locationsFilter.trim()) && (
                  <div className="pt-4 border-t border-[#EDEDED]">
                    <label className="block text-sm font-medium text-[#015FA6] mb-2">
                      Generated Search Query:
                    </label>
                    <div className="px-4 py-3 bg-white rounded-lg text-[#515151] text-sm border border-[#EDEDED]">
                      {generateQueryFromFilters() || 'Enter a query or select filters...'}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-white rounded-2xl p-4 border-l-4 border-red-500">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="bg-white rounded-2xl p-12 text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#015FA6]"></div>
            <p className="mt-4 text-[#757575]">Searching candidates...</p>
          </div>
        )}

        {/* Search Results */}
        {searchResults && !loading && (() => {
          const totalPages = Math.ceil(searchResults.matches.length / resultsPerPage);
          const startIndex = (currentPage - 1) * resultsPerPage;
          const endIndex = startIndex + resultsPerPage;
          const currentResults = searchResults.matches.slice(startIndex, endIndex);

          return (
            <div className="bg-white rounded-2xl p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-[#0B111E]">
                  Search Results: {searchResults.totalCandidates} candidates
                </h3>
                <p className="text-sm text-[#757575]">
                  Processing time: {searchResults.processingTimeMs}ms
                </p>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-[#F9F9F9] border-b border-[#EDEDED]">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-[#515151]">Rank</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-[#515151]">Name</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-[#515151]">Email</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-[#515151]">Experience</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-[#515151]">Skills</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-[#515151]">Titles</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-[#515151]">Companies</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-[#515151]">Match Score</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentResults.map((match, index) => (
                      <tr
                        key={match.candidate.id}
                        className="border-b border-[#EDEDED] hover:bg-[#F9F9F9] transition-colors"
                      >
                        <td className="px-4 py-3 text-sm text-[#0B111E]">{startIndex + index + 1}</td>
                        <td className="px-4 py-3 text-sm font-medium text-[#0B111E]">
                          {match.candidate.name || 'N/A'}
                        </td>
                        <td className="px-4 py-3 text-sm text-[#757575]">
                          {match.candidate.email}
                        </td>
                        <td className="px-4 py-3 text-sm text-[#0B111E]">
                          {match.candidate.yearsExp} years
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-1">
                            {match.candidate.skills && match.candidate.skills.length > 0 ? (
                              <>
                                {match.candidate.skills.slice(0, 3).map((skill, idx) => (
                                  <span
                                    key={idx}
                                    className="px-2 py-1 bg-[#E3F2FD] text-[#015FA6] text-xs rounded"
                                  >
                                    {skill}
                                  </span>
                                ))}
                                {match.candidate.skills.length > 3 && (
                                  <span className="px-2 py-1 text-xs text-[#757575]">
                                    +{match.candidate.skills.length - 3}
                                  </span>
                                )}
                              </>
                            ) : (
                              <span className="text-xs text-[#A5A5A5]">No skills listed</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-[#757575]">
                          {match.candidate.allJobTitles && match.candidate.allJobTitles.length > 0 ? (
                            <>
                              {match.candidate.allJobTitles.slice(0, 2).join(', ')}
                              {match.candidate.allJobTitles.length > 2 && '...'}
                            </>
                          ) : (
                            'N/A'
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-[#757575]">
                          {match.candidate.allCompanies && match.candidate.allCompanies.length > 0 ? (
                            <>
                              {match.candidate.allCompanies.slice(0, 2).join(', ')}
                              {match.candidate.allCompanies.length > 2 && '...'}
                            </>
                          ) : (
                            'N/A'
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-center">
                            <div className="font-bold text-[#015FA6]">
                              {match.matchScore.toFixed(2)}
                            </div>
                            <div className="text-xs text-[#A5A5A5] mt-1 space-y-0.5">
                              <div>Vec: {match.vectorSimilarity.toFixed(3)}</div>
                              <div>Soft: {match.softCriteriaScore.toFixed(1)}</div>
                              {match.qwenScore !== undefined && (
                                <div>Qwen: {match.qwenScore.toFixed(1)}</div>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {searchResults.totalCandidates > 0 && totalPages > 1 && (
                <div className="mt-6 flex justify-center items-center gap-4">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="px-4 py-2 bg-[#F1F3F4] hover:bg-[#E3F2FD] disabled:bg-[#F9F9F9] disabled:cursor-not-allowed text-[#0B111E] rounded-lg transition-colors text-sm font-medium"
                  >
                    Previous
                  </button>
                  <span className="text-sm text-[#757575]">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    className="px-4 py-2 bg-[#F1F3F4] hover:bg-[#E3F2FD] disabled:bg-[#F9F9F9] disabled:cursor-not-allowed text-[#0B111E] rounded-lg transition-colors text-sm font-medium"
                  >
                    Next
                  </button>
                </div>
              )}

              {searchResults.totalCandidates === 0 && (
                <div className="text-center py-12">
                  <p className="text-lg text-[#757575]">No candidates found matching your query.</p>
                  <p className="mt-2 text-sm text-[#A5A5A5]">Try adjusting your search criteria or using a template.</p>
                </div>
              )}
            </div>
          );
        })()}

        {/* Info Modal */}
        {showInfoModal && infoSeedKey && (
          <div
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowInfoModal(false)}
          >
            <div
              className="bg-white rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-xl font-bold text-[#0B111E]">
                  {QUERY_SEED_LABELS[infoSeedKey]} - Search Criteria
                </h3>
                <button
                  onClick={() => setShowInfoModal(false)}
                  className="text-[#757575] hover:text-[#0B111E] transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold text-[#015FA6] mb-2 text-sm">Search Query:</h4>
                  <p className="text-sm text-[#515151] bg-[#F9F9F9] p-3 rounded-lg border border-[#EDEDED]">{QUERY_SEEDS[infoSeedKey].query}</p>
                </div>

                <div>
                  <h4 className="font-semibold text-red-600 mb-2 text-sm">Hard Criteria (Must Match):</h4>
                  {QUERY_SEEDS[infoSeedKey].hardCriteria && Object.keys(QUERY_SEEDS[infoSeedKey].hardCriteria || {}).length > 0 ? (
                    <div className="bg-[#F9F9F9] p-3 rounded-lg border border-[#EDEDED] space-y-2 text-sm">
                      {QUERY_SEEDS[infoSeedKey].hardCriteria?.minYearsExperience && (
                        <div><span className="font-medium text-[#0B111E]">Min Experience:</span> <span className="text-[#515151]">{QUERY_SEEDS[infoSeedKey].hardCriteria?.minYearsExperience} years</span></div>
                      )}
                      {QUERY_SEEDS[infoSeedKey].hardCriteria?.requiredDegrees && (
                        <div><span className="font-medium text-[#0B111E]">Required Degrees:</span> <span className="text-[#515151]">{QUERY_SEEDS[infoSeedKey].hardCriteria?.requiredDegrees?.join(', ')}</span></div>
                      )}
                      {QUERY_SEEDS[infoSeedKey].hardCriteria?.requiredSkills && (
                        <div><span className="font-medium text-[#0B111E]">Required Skills:</span> <span className="text-[#515151]">{QUERY_SEEDS[infoSeedKey].hardCriteria?.requiredSkills?.join(', ')}</span></div>
                      )}
                      {QUERY_SEEDS[infoSeedKey].hardCriteria?.requiredLocations && (
                        <div><span className="font-medium text-[#0B111E]">Required Locations:</span> <span className="text-[#515151]">{QUERY_SEEDS[infoSeedKey].hardCriteria?.requiredLocations?.join(', ')}</span></div>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-[#A5A5A5] bg-[#F9F9F9] p-3 rounded-lg border border-[#EDEDED]">No hard criteria filters</p>
                  )}
                </div>

                <div>
                  <h4 className="font-semibold text-green-600 mb-2 text-sm">Soft Criteria (Boosts Score):</h4>
                  {QUERY_SEEDS[infoSeedKey].softCriteria && Object.keys(QUERY_SEEDS[infoSeedKey].softCriteria || {}).length > 0 ? (
                    <div className="bg-[#F9F9F9] p-3 rounded-lg border border-[#EDEDED] space-y-2 text-sm">
                      {QUERY_SEEDS[infoSeedKey].softCriteria?.keywords && (
                        <div>
                          <span className="font-medium text-[#0B111E]">Keywords:</span>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {QUERY_SEEDS[infoSeedKey].softCriteria?.keywords?.map((kw: string, idx: number) => (
                              <span key={idx} className="px-2 py-1 bg-[#E3F2FD] text-[#015FA6] text-xs rounded">{kw}</span>
                            ))}
                          </div>
                        </div>
                      )}
                      {QUERY_SEEDS[infoSeedKey].softCriteria?.preferredTitles && (
                        <div><span className="font-medium text-[#0B111E]">Preferred Titles:</span> <span className="text-[#515151]">{QUERY_SEEDS[infoSeedKey].softCriteria?.preferredTitles?.join(', ')}</span></div>
                      )}
                      {QUERY_SEEDS[infoSeedKey].softCriteria?.preferredCompanies && (
                        <div><span className="font-medium text-[#0B111E]">Preferred Companies:</span> <span className="text-[#515151]">{QUERY_SEEDS[infoSeedKey].softCriteria?.preferredCompanies?.join(', ')}</span></div>
                      )}
                      {QUERY_SEEDS[infoSeedKey].softCriteria?.preferredSchools && (
                        <div><span className="font-medium text-[#0B111E]">Preferred Schools:</span> <span className="text-[#515151]">{QUERY_SEEDS[infoSeedKey].softCriteria?.preferredSchools?.join(', ')}</span></div>
                      )}
                      {QUERY_SEEDS[infoSeedKey].softCriteria?.preferMoreExperience !== undefined && (
                        <div><span className="font-medium text-[#0B111E]">Prefer More Experience:</span> <span className="text-[#515151]">{QUERY_SEEDS[infoSeedKey].softCriteria?.preferMoreExperience ? 'Yes' : 'No'}</span></div>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-[#A5A5A5] bg-[#F9F9F9] p-3 rounded-lg border border-[#EDEDED]">No soft criteria</p>
                  )}
                </div>
              </div>

              <div className="mt-6 text-center">
                <button
                  onClick={() => setShowInfoModal(false)}
                  className="px-6 py-2 bg-[#015FA6] hover:bg-[#014A85] text-white rounded-lg transition-colors text-sm font-medium"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </CompanyLayout>
  );
};

export default CandidateSearch;
